'use client';

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Key,
  Download,
  Upload,
  Check,
  X,
  Warning,
  Spinner,
  Copy,
  Fingerprint,
  Shield,
  Clock,
  CaretRight,
} from '@phosphor-icons/react';
import { licenseApi } from '../services/api';
import { useToastActions } from '../components/ui/Toast';
import type { LicenseInfo, MachineFingerprint } from '../types';

interface LicensePageProps {
  onBack?: () => void;
}

export function LicensePage({ onBack }: LicensePageProps) {
  const { t } = useTranslation();
  const [license, setLicense] = useState<LicenseInfo | null>(null);
  const [machine, setMachine] = useState<MachineFingerprint | null>(null);
  const [validation, setValidation] = useState<{ isValid: boolean; status: string; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToastActions();

  // 加载 License 状态
  const loadLicenseStatus = async () => {
    try {
      setIsLoading(true);
      const response = await licenseApi.getLicenseStatus();
      if (response.code === 0 && response.data) {
        setLicense(response.data.license);
        setMachine(response.data.machine);
        setValidation(response.data.validation);
      } else {
        toast.error(response.message || t('license.fetchStatusFailed'));
      }
    } catch (error) {
      console.error('Failed to load license status:', error);
      toast.error(t('license.fetchStatusFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLicenseStatus();
  }, []);

  // 下载机器指纹
  const handleDownloadFingerprint = async () => {
    try {
      setIsDownloading(true);
      const response = await licenseApi.downloadFingerprint();
      if (!response.ok) {
        throw new Error(t('license.downloadFailed'));
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fingerprint-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('license.downloadSuccess'));
    } catch (error) {
      console.error('Failed to download fingerprint:', error);
      toast.error(t('license.downloadFailed'));
    } finally {
      setIsDownloading(false);
    }
  };

  // 复制机器指纹
  const handleCopyFingerprint = () => {
    if (machine?.hash) {
      navigator.clipboard.writeText(machine.hash);
      toast.success(t('license.copySuccess'));
    }
  };

  // 上传 License
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.name.endsWith('.zip')) {
      toast.error(t('license.uploadZipOnly'));
      return;
    }

    setIsUploading(true);
    try {
      const response = await licenseApi.uploadLicense(file);
      if (response.code === 0) {
        toast.success(t('license.uploadSuccess'));
        await loadLicenseStatus();
      } else {
        toast.error(response.message || t('license.uploadFailed'));
      }
    } catch (error) {
      console.error('Failed to upload license:', error);
      toast.error(t('license.uploadFailed'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 刷新 License
  const handleRefresh = async () => {
    try {
      const response = await licenseApi.refreshLicense();
      if (response.code === 0) {
        toast.success(t('license.refreshSuccess'));
        await loadLicenseStatus();
      } else {
        toast.error(response.message || t('license.refreshFailed'));
      }
    } catch (error) {
      console.error('Failed to refresh license:', error);
      toast.error(t('license.refreshFailed'));
    }
  };

  // 获取状态颜色
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'text-emerald-600 bg-emerald-50 border-emerald-100';
      case 'grace_period':
        return 'text-amber-600 bg-amber-50 border-amber-100';
      case 'expired':
      case 'revoked':
      case 'invalid':
        return 'text-red-600 bg-red-50 border-red-100';
      default:
        return 'text-zinc-600 bg-zinc-50 border-zinc-200';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'active':
        return t('license.valid');
      case 'grace_period':
        return t('license.gracePeriod');
      case 'expired':
        return t('license.expired');
      case 'revoked':
        return t('license.revoked');
      case 'invalid':
        return t('license.invalid');
      default:
        return t('common.unknown');
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active':
        return <Check className="w-4 h-4" weight="bold" />;
      case 'grace_period':
        return <Warning className="w-4 h-4" weight="bold" />;
      case 'expired':
      case 'revoked':
      case 'invalid':
        return <X className="w-4 h-4" weight="bold" />;
      default:
        return <Warning className="w-4 h-4" weight="bold" />;
    }
  };

  if (isLoading) {
    return (
      <div className="h-full bg-zinc-50 flex items-center justify-center">
        <Spinner className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        className="max-w-3xl mx-auto p-6"
      >
        {/* 头部 */}
        {onBack && (
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={onBack}
              className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
            >
              <CaretRight className="w-5 h-5 text-zinc-600 rotate-180" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">{t('license.title')}</h1>
              <p className="text-sm text-zinc-500">{t('license.subtitle')}</p>
            </div>
          </div>
        )}

        {!onBack && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-zinc-900">{t('license.title')}</h1>
            <p className="text-sm text-zinc-500 mt-1">{t('license.subtitle')}</p>
          </div>
        )}

        {/* 授权状态卡片 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Key className="w-6 h-6 text-emerald-600" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">{t('license.status')}</h2>
                <p className="text-sm text-zinc-500">
                  {license ? `${t('license.licenseId')}: ${license.licenseId}` : t('license.notActivated')}
                </p>
              </div>
            </div>
            {license && (
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(license.status)}`}>
                {getStatusIcon(license.status)}
                {getStatusText(license.status)}
              </div>
            )}
          </div>

          {license ? (
            <div className="space-y-4">
              {/* 有效期 */}
              <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <Clock className="w-4 h-4" />
                  {t('license.expireDate')}
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-zinc-900">
                    {new Date(license.expireAt).toLocaleDateString('zh-CN')}
                  </p>
                  <p className={`text-xs ${license.remainingDays > 30 ? 'text-emerald-600' : license.remainingDays > 7 ? 'text-amber-600' : 'text-red-600'}`}>
                    {t('license.remainingDays', { days: license.remainingDays })}
                  </p>
                </div>
              </div>

              {/* 离线宽限期 */}
              <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <Shield className="w-4 h-4" />
                  {t('license.offlineGrace')}
                </div>
                <span className="text-sm font-medium text-zinc-900">{license.offlineGraceDays} {t('common.days')}</span>
              </div>

              {/* 最大用户数 */}
              {license.maxUsers && (
                <div className="flex items-center justify-between py-3 border-b border-zinc-100">
                  <div className="flex items-center gap-2 text-sm text-zinc-600">
                    <Shield className="w-4 h-4" />
                    {t('license.maxUsers')}
                  </div>
                  <span className="text-sm font-medium text-zinc-900">{license.maxUsers} {t('common.people')}</span>
                </div>
              )}

              {/* 功能授权 */}
              <div className="pt-2">
                <p className="text-sm font-medium text-zinc-700 mb-3">{t('license.authorizedFeatures')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(license.features || {}).map(([key, enabled]) => (
                    <div
                      key={key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                        enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-50 text-zinc-400'
                      }`}
                    >
                      {enabled ? (
                        <Check className="w-4 h-4" weight="bold" />
                      ) : (
                        <X className="w-4 h-4" weight="bold" />
                      )}
                      <span className="capitalize">
                        {key === 'basic' && t('license.basic')}
                        {key === 'advanced' && t('license.advanced')}
                        {key === 'premium' && t('license.premium')}
                        {key === 'admin' && t('license.admin')}
                        {key === 'api' && t('license.api')}
                        {key === 'export' && t('license.export')}
                        {key === 'import' && t('license.import')}
                        {key === 'backup' && t('license.backup')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 验证信息 */}
              {validation && (
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  validation.isValid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                }`}>
                  <div className="flex items-center gap-2">
                    {validation.isValid ? (
                      <Check className="w-4 h-4" weight="bold" />
                    ) : (
                      <Warning className="w-4 h-4" weight="bold" />
                    )}
                    <span className="font-medium">{validation.message}</span>
                  </div>
                </div>
              )}

              {/* 刷新按钮 */}
              <button
                onClick={handleRefresh}
                className="w-full mt-4 py-2.5 text-sm font-medium rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
              >
                {t('license.refresh')}
              </button>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-4">
                <Key className="w-8 h-8 text-zinc-400" />
              </div>
              <p className="text-zinc-500 mb-2">{t('license.notActivatedDesc')}</p>
              <p className="text-sm text-zinc-400">{t('license.activatePrompt')}</p>
            </div>
          )}
        </div>

        {/* 机器指纹 */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Fingerprint className="w-5 h-5 text-blue-600" weight="fill" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">{t('license.machineFingerprint')}</h2>
              <p className="text-sm text-zinc-500">{t('license.fingerprintDesc')}</p>
            </div>
          </div>

          {machine ? (
            <div className="space-y-3">
              {/* 指纹哈希 */}
              <div className="p-3 bg-zinc-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-zinc-500">{t('license.fingerprintHash')}</span>
                  <button
                    onClick={handleCopyFingerprint}
                    className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    {t('common.copy')}
                  </button>
                </div>
                <code className="text-xs text-zinc-700 break-all font-mono">
                  {machine.hash}
                </code>
              </div>

              {/* 硬件信息 */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">{t('license.platform')}</p>
                  <p className="text-sm font-medium text-zinc-900 capitalize">{machine.platform}</p>
                </div>
                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-1">{t('license.cpu')}</p>
                  <p className="text-sm font-medium text-zinc-900 truncate">{machine.components.cpu}</p>
                </div>
              </div>

              {/* 下载按钮 */}
              <button
                onClick={handleDownloadFingerprint}
                disabled={isDownloading}
                className="w-full py-2.5 text-sm font-medium rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <Spinner className="w-4 h-4 animate-spin" />
                    {t('common.downloading')}
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {t('license.downloadFingerprint')}
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-zinc-400">{t('license.fingerprintUnavailable')}</p>
            </div>
          )}
        </div>

        {/* 上传 License */}
        <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Upload className="w-5 h-5 text-amber-600" weight="fill" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">{t('license.uploadLicense')}</h2>
              <p className="text-sm text-zinc-500">{t('license.uploadLicenseDesc')}</p>
            </div>
          </div>

          <div
            onClick={handleUploadClick}
            className="border-2 border-dashed border-zinc-200 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-3">
              <Upload className="w-6 h-6 text-zinc-400" />
            </div>
            <p className="text-sm font-medium text-zinc-700 mb-1">
              {isUploading ? t('common.uploading') : t('license.uploadPrompt')}
            </p>
            <p className="text-xs text-zinc-400">{t('license.uploadHint')}</p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* 说明 */}
        <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">{t('license.instructions')}</p>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• {t('license.instruction1')}</li>
                <li>• {t('license.instruction2')}</li>
                <li>• {t('license.instruction3')}</li>
                <li>• {t('license.instruction4')}</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
